import { Stack, SxProps, Tab, Tabs, Theme, useTheme } from '@mui/material';
import { Dispatch, ReactElement, SetStateAction } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import { BlockContent, BlockHeader } from 'src/components/Layout/named-layout-block.view';

interface SidePanelTabsProps<T> {
  tabs: {
    value: string;
    label: string;
    icon?: string;
    iconLibrary?: IconLibrary;
    disabled?: boolean;
  }[];
  activeTab: T;
  setActiveTab: Dispatch<SetStateAction<T>>;
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  showLabel?: boolean;
  hasBorderTop?: boolean;
  isNested?: boolean;
  sx?: SxProps<Theme>;
  innerSx?: SxProps<Theme>;
  tabSx?: SxProps<Theme>;
  children: ReactElement | null;
}

export const SidePanelTabs = <T,>({
  tabs,
  activeTab,
  showLabel,
  setActiveTab,
  variant = 'standard',
  hasBorderTop = true,
  isNested = false,
  sx,
  tabSx,
  innerSx,
  children,
}: SidePanelTabsProps<T>) => {
  const theme = useTheme();

  return (
    <Stack
      sx={{
        minHeight: 0,
        flexGrow: 1,
        ...sx,
      }}
    >
      <BlockHeader
        sx={{
          height: 'auto',
          padding: theme.spacing(0, 1),
          borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
          borderTop: hasBorderTop
            ? `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`
            : 'none',

          [theme.breakpoints.up('sm')]: {
            marginInline: isNested ? theme.spacing(-1) : 0,
          },
        }}
      >
        <Tabs
          variant={variant}
          sx={{
            width: variant === 'fullWidth' ? '100%' : 'auto',
            height: '100%',
          }}
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue as T)}
          scrollButtons={false}
          TabIndicatorProps={{
            sx: {
              backgroundColor: theme.palette.custom.OV.sys.primary,
            },
          }}
        >
          {tabs.map((el) => (
            <Tab
              disabled={el.disabled}
              key={el.value}
              iconPosition="start"
              sx={{
                ...tabSx,
                color: theme.palette.custom.OV.sys.onSurface,
                '&.Mui-selected': {
                  color: theme.palette.custom.OV.sys.primary,
                },
                minHeight: theme.spacing(6),
                minWidth: 'auto',
              }}
              label={showLabel ? el.label : undefined}
              icon={
                el.icon ? (
                  <Tooltip title={el.label}>
                    <DynamicIcon
                      name={el.icon}
                      library={el.iconLibrary}
                      sx={{
                        color: theme.palette.custom.OV.sys.onSurface,
                        width: '1.5rem',
                        height: '1.5rem',
                      }}
                    />
                  </Tooltip>
                ) : undefined
              }
              value={el.value}
              aria-label={el.label}
            />
          ))}
        </Tabs>
      </BlockHeader>
      <BlockContent
        sx={{
          marginInline: isNested ? theme.spacing(-1) : 0,
          position: 'static',
          minHeight: 0,
          ...innerSx,
        }}
      >
        {children}
      </BlockContent>
    </Stack>
  );
};
